import { EnumAttachmentErrorCode } from '../enums/attachment-error-code.enum';
import { EnumAttachmentStatus } from '../enums/attachment-status.enum';
import { AttachmentAccessMode } from './message-attachment.interface';

export const MAX_ATTACHMENTS_PER_MESSAGE = 5;

/* eslint-disable @typescript-eslint/naming-convention */
export interface IUserAttachmentRaw {
  type?: string;
  attachment_id: string;
  name: string;
  mime: string;
  size_bytes: number;
  created_at: string;
  owner_user_id?: number;
  status: string;
  expires_at?: string | null;
  error_code?: string | null;
  error_message?: string | null;
  access_mode: AttachmentAccessMode;
  download_url?: string;
}
/* eslint-enable @typescript-eslint/naming-convention */

export interface IUserAttachment {
  source: 'user';
  attachmentId: string;
  name: string;
  mime: string;
  sizeBytes: number;
  createdAt: string;
  status: EnumAttachmentStatus;
  expiresAt: string | null;
  errorCode: EnumAttachmentErrorCode | null;
  errorMessage: string | null;
  accessMode: AttachmentAccessMode;
}

const toAttachmentStatus = (raw: string): EnumAttachmentStatus => {
  const known = Object.values(EnumAttachmentStatus);
  return known.find((status) => status === raw) ?? EnumAttachmentStatus.Unknown;
};

const toAttachmentErrorCode = (
  raw: string | null | undefined
): EnumAttachmentErrorCode | null => {
  const known = Object.values(EnumAttachmentErrorCode);
  return known.find((code) => code === raw) ?? null;
};

export const toUserAttachment = (raw: IUserAttachmentRaw): IUserAttachment => ({
  source: 'user',
  attachmentId: raw.attachment_id,
  name: raw.name,
  mime: raw.mime,
  sizeBytes: raw.size_bytes,
  createdAt: raw.created_at,
  status: toAttachmentStatus(raw.status),
  expiresAt: raw.expires_at ?? null,
  errorCode: toAttachmentErrorCode(raw.error_code),
  errorMessage: raw.error_message ?? null,
  accessMode: raw.access_mode
});

const isAttachmentExpired = (
  attachment: IUserAttachment,
  now = Date.now()
): boolean => {
  if (!attachment.expiresAt) {
    return false;
  }
  const expiresAt = new Date(attachment.expiresAt).getTime();
  return !Number.isNaN(expiresAt) && expiresAt <= now;
};



export const isAttachmentDownloadable = (
  attachment: IUserAttachment,
  now = Date.now()
): boolean =>
  attachment.status === EnumAttachmentStatus.Ready &&
  !isAttachmentExpired(attachment, now);
